import { useEffect, useState } from "react";
import { useWoStatuses } from "utils/woStatus";
import authHeader from "services/auth-header";
import API from "services/axios";

// The actual values stored in WorkOrders.WOStatus -- confirmed against the DB,
// not assumed. Add here if new ones are introduced.
// Mirrors the whitelist in updateAdminReviewed on the API side. "hold" is the
// option added alongside the Approvals Admin dropdown.
const ADMIN_REVIEWED_OPTIONS = ["pending", "approved", "rejected", "hold"];

const fullName = (person) =>
  `${person?.firstName || ""} ${person?.lastName || ""}`.trim();

export default function WOSearchFields({ formData, handleInputChange }) {
  // Settings -> WO Statuses (user, 2026-10-29).
  const woStatuses = useWoStatuses();
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [shipmentMethods, setShipmentMethods] = useState([]);
  const [taxRates, setTaxRates] = useState([]);

  useEffect(() => {
    let cancelled = false;

    // Every lookup is optional: a failed one just leaves that dropdown empty
    // rather than blocking the whole form, so they're settled independently.
    const load = async (request, apply) => {
      try {
        const { data } = await request();
        if (!cancelled) apply(data);
      } catch {
        /* dropdown stays empty */
      }
    };

    // One endpoint already serves users / sales persons / payment terms /
    // shipment methods -- the same one the WO Details panel uses.
    load(
      () => API.get("/work-order/drop-down-details", { headers: authHeader() }),
      (data) => {
        setUsers(data?.result?.usersList || []);
        setSalesPersons(data?.result?.salesPersonsList || []);
        setPaymentTerms(data?.result?.paymentTermsList || []);
        setShipmentMethods(data?.result?.shipmentMethodsList || []);
      },
    );
    load(
      () => API.get("/locations", { headers: authHeader() }),
      (data) => setLocations(data || []),
    );
    load(
      () => API.get("/taxrate", { headers: authHeader() }),
      (data) => setTaxRates(Array.isArray(data) ? data : data?.result || []),
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const text = (name, label, type = "text") => (
    <div className="col-md-4 mb-2">
      <label className="form-label mb-1">{label}</label>
      <input
        type={type}
        name={name}
        className="form-control"
        value={formData?.[name] || ""}
        onChange={handleInputChange}
      />
    </div>
  );

  // Plain string options (status enums).
  // options: strings, or { value, label }.
  const select = (name, label, options) => (
    <div className="col-md-4 mb-2">
      <label className="form-label mb-1">{label}</label>
      <select
        name={name}
        className="form-select"
        value={formData?.[name] || ""}
        onChange={handleInputChange}
      >
        <option value="">Any</option>
        {options.map((option) =>
          typeof option === "string" ? (
            <option key={option} value={option}>
              {option.charAt(0).toUpperCase() + option.slice(1)}
            </option>
          ) : (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ),
        )}
      </select>
    </div>
  );

  /**
   * Dropdown over a lookup list. `getValue`/`getLabel` keep this usable for
   * lists whose id and display columns differ. `noneLabel` adds the -1 "not
   * set" option for nullable foreign keys.
   */
  const lookupSelect = (
    name,
    label,
    list,
    getLabel,
    { getValue = (item) => item.id, noneLabel } = {},
  ) => (
    <div className="col-md-4 mb-2">
      <label className="form-label mb-1">{label}</label>
      <select
        name={name}
        className="form-select"
        value={formData?.[name] || ""}
        onChange={handleInputChange}
      >
        <option value="">Any</option>
        {/* -1 is the backend's "not set" sentinel. */}
        {noneLabel && <option value="-1">{noneLabel}</option>}
        {list?.map((item, index) => (
          <option key={getValue(item) ?? index} value={getValue(item)}>
            {getLabel(item)}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <h4 className="border-bottom border-2 d-inline-block mb-1 pe-2">
        Work Order
      </h4>
      <div className="row bg-white custom-border-radius-sm custom-shadow mx-1 py-2">
        {text("woId", "W-ID", "number")}
        {text("customerName", "Customer Name")}
        {text("jobName", "Job Name")}
        {text("subJobName", "Sports Name (Sub Job)")}

        {/* createdBy is a plain name string on the row, not a user id -- the
            dropdown submits the user's display name so it matches what was
            written at creation time. */}
        {lookupSelect("createdBy", "Created By", users, fullName, {
          getValue: fullName,
        })}

        {lookupSelect(
          "locationId",
          "Brand",
          locations,
          (loc) => loc.locationName,
          { noneLabel: "No Brand" },
        )}
      </div>

      <h4 className="border-bottom border-2 d-inline-block mb-1 pe-2 mt-3">
        WO Details
      </h4>
      <div className="row bg-white custom-border-radius-sm custom-shadow mx-1 py-2">
        {lookupSelect(
          "insideSalesPersonId",
          "Inside Sales Person",
          users,
          fullName,
          { noneLabel: "None" },
        )}
        {lookupSelect(
          "salesPersonId",
          "Sales Person",
          salesPersons,
          (person) => person.name,
          { noneLabel: "None" },
        )}
        {lookupSelect(
          "paymentTermsId",
          "Payment Term",
          paymentTerms,
          (term) => term.termName,
          { noneLabel: "None" },
        )}
        {lookupSelect(
          "taxRateId",
          "Tax Rate",
          taxRates,
          (rate) => `${rate.taxAccountName || ""} (${rate.taxRate})`.trim(),
          { noneLabel: "None" },
        )}
        {lookupSelect(
          "shipmentMethod",
          "Shipment Method",
          shipmentMethods,
          (method) => method.shippingMethod,
          { noneLabel: "None" },
        )}

        <div className="col-md-4 mb-2">
          <label className="form-label mb-1">Invoiced</label>
          <select
            name="invoiceReady"
            className="form-select"
            value={formData?.invoiceReady || ""}
            onChange={handleInputChange}
          >
            <option value="">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      </div>

      <h4 className="border-bottom border-2 d-inline-block mb-1 pe-2 mt-3">
        Status
      </h4>
      <div className="row bg-white custom-border-radius-sm custom-shadow mx-1 py-2">
        {select(
          "WOStatus",
          "WO Status",
          woStatuses.map((s) => ({ value: s.value, label: s.name })),
        )}
        {select("adminReviewed", "Admin Reviewed", ADMIN_REVIEWED_OPTIONS)}
      </div>

      <h4 className="border-bottom border-2 d-inline-block mb-1 pe-2 mt-3">
        Contact
      </h4>
      <div className="row bg-white custom-border-radius-sm custom-shadow mx-1 py-2">
        {text("contactFirstName", "First Name")}
        {text("contactLastName", "Last Name")}
        {text("contactDepartment", "Department")}
        {text("contactPhone", "Phone")}
        {text("contactEmail", "Email")}
      </div>

      <h4 className="border-bottom border-2 d-inline-block mb-1 pe-2 mt-3">
        Dates
      </h4>
      <div className="row bg-white custom-border-radius-sm custom-shadow mx-1 py-2">
        {/* Due date replaces the standalone range that used to sit above the
            list. Either bound works on its own, for both ranges. */}
        {text("dueDateFrom", "Due Date From", "date")}
        {text("dueDateTo", "Due Date To", "date")}
        <div className="col-md-4 mb-2"></div>
        {text("woDateFrom", "WO Date From", "date")}
        {text("woDateTo", "WO Date To", "date")}
      </div>

      <h4 className="border-bottom border-2 d-inline-block mb-1 pe-2 mt-3">
        Notes
      </h4>
      <div className="row bg-white custom-border-radius-sm custom-shadow mx-1 py-2">
        {text("notes", "Notes")}
      </div>
    </div>
  );
}
